import React, { useCallback, useContext, useEffect, useState } from 'react';
import cn from 'classnames';
import FocusTrap from 'focus-trap-react';
import { ThemeOverride, ThemeSurface } from '@wix/da-react-context/pkg/ThemeContext';
import { DropdownStateContext } from '@wix/da-ds/pkg/Dropdown/DropdownStateManager';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import { useFriendList } from '../../utils/hooks/useFriendList';
import { useListKeyboardNavigation } from '../../utils/hooks/useListKeyboardNavigation';
import UserInput from '../_partials/UserInput';
import UserList from '../_partials/UserList';
import s from './DeviantsListCard.scss';
const DeviantsListCard = ({ className, contentClassName, selectedUserId, itemSuffix, showSuffixOnlyOnHover, emptyView, onItemClick, onClose })=>{
    const [searchTerm, setSearchTerm] = useState('');
    const { closeDropdown } = useContext(DropdownStateContext);
    const { users, streamId, isLoading, isInitialized } = useFriendList(searchTerm);
    const onKeyboardItemSelected = useCallback((selectedIndex)=>onItemClick && users[selectedIndex] && onItemClick(users[selectedIndex]), [
        onItemClick,
        users
    ]);
    const { onKeyEvent, selectedIndex, resetSelectedIndex, selectFirstIndex } = useListKeyboardNavigation(users.length, onKeyboardItemSelected, onClose !== null && onClose !== void 0 ? onClose : closeDropdown);
    // Reset the selected index to nothing when our list is loading,
    // or to the first item if the user is actively searching.
    useEffect(()=>{
        if (isLoading) {
            resetSelectedIndex();
        } else if (!isLoading && isInitialized && searchTerm.length >= 3) {
            selectFirstIndex();
        }
    }, [
        isLoading,
        isInitialized,
        resetSelectedIndex,
        searchTerm,
        selectFirstIndex
    ]);
    // For use with aria-activedescendant
    const generateItemId = useCallback((index)=>`deviation-list-item-${index}`, []);
    return /*#__PURE__*/ React.createElement("div", {
        className: cn('ds-surface-tertiary', s['root'], className)
    }, /*#__PURE__*/ React.createElement(ThemeOverride, {
        className: s['theme-wrapper'],
        themeSurface: ThemeSurface.TERTIARY
    }, /*#__PURE__*/ React.createElement(FocusTrap, {
        focusTrapOptions: {
            clickOutsideDeactivates: true
        }
    }, /*#__PURE__*/ React.createElement("div", {
        className: cn(s['content'], s['inputs-updated'], contentClassName),
        onKeyDown: onKeyEvent,
        // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
        role: "combobox",
        "aria-haspopup": "listbox",
        "aria-expanded": "true"
    }, /*#__PURE__*/ React.createElement("div", {
        className: s['input-wrapper']
    }, /*#__PURE__*/ React.createElement(UserInput, {
        value: searchTerm,
        onChange: setSearchTerm,
        className: s['filter-input'],
        /*
              The conditional 'none' here is in response to the below part of the WAI-ARIA spec. '>= 3' because we only
              start the search at greater-than-or-equal to 3 characters.

              > The aria-autocomplete property is limited to describing predictive behaviors of an input element.
              > Authors SHOULD [...] set aria-autocomplete to none if an input element provides one or more input proposals
              > where none of the proposals are dependent on the specific input provided by the user.

              https://www.w3.org/TR/wai-aria-1.1/#aria-autocomplete
            */ "aria-autocomplete": searchTerm.length >= 3 ? 'list' : 'none',
        "aria-activedescendant": selectedIndex !== undefined ? generateItemId(selectedIndex) : undefined,
        "aria-controls": "deviants-card-user-list"
    })), isInitialized && /*#__PURE__*/ React.createElement(UserList, {
        id: "deviants-card-user-list",
        itemIdGenerator: generateItemId,
        streamId: streamId,
        users: users,
        className: s['list'],
        isLoading: isLoading,
        selectedUserId: selectedUserId,
        itemSuffix: itemSuffix,
        showSuffixOnlyOnHover: showSuffixOnlyOnHover,
        keyboardSelectedIndex: selectedIndex,
        onItemClick: onItemClick,
        emptyView: emptyView
    }), !isInitialized && /*#__PURE__*/ React.createElement(LoadingIndicator, null)))));
};
DeviantsListCard.displayName = 'DeviantsListCard';
export default DeviantsListCard;
